import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus } from 'lucide-react';
import {
  Heading,
  Text,
  Stack,
  Container,
} from '@/components/ui/index.js';
import { BottomNav } from '@/components/navigation/index.js';
import { useSocialStore } from '../../store/useSocialStore.js';
import {
  FriendCard,
  ActivityItem,
  GroupCard,
  SuggestionCard,
  QuickAction,
} from './components/index.js';
import styles from './SocialPage.module.css';

/**
 * SocialPage - Social features hub with friends list, activity feed, 
 * group discovery, and quick actions for connecting with other athletes.
 */
export function SocialPage() {
  const navigate = useNavigate();
  const {
    friends,
    activityFeed,
    groups,
    suggestions,
    pendingInvites,
    addFriend,
  } = useSocialStore();

  const [isLoading, setIsLoading] = useState(true);
  const [addedSuggestions, setAddedSuggestions] = useState([]);

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  // Handle friend message
  const handleMessageFriend = (friendId) => {
    console.log(`Open chat with friend ${friendId}`);
    // In a real app, this would navigate to chat or open a modal
  };

  // Handle more options
  const handleMoreOptions = (friendId) => {
    console.log(`Show more options for friend ${friendId}`);
  };

  // Handle add friend from suggestions
  const handleAddFriend = (suggestionId) => {
    setAddedSuggestions(prev => [...prev, suggestionId]);
    // Simulate API call delay
    setTimeout(() => {
      addFriend(suggestionId);
    }, 500);
  };

  // Handle group click
  const handleGroupClick = (groupId) => {
    console.log(`Navigate to group ${groupId}`);
    // navigate(`/groups/${groupId}`);
  };

  // Handle discover groups
  const handleDiscoverGroups = () => {
    console.log('Navigate to discover groups');
  };

  // Handle see all friends
  const handleSeeAllFriends = () => {
    console.log('Navigate to all friends');
  };

  // Handle activity action
  const handleActivityAction = (activityId) => {
    console.log(`View activity ${activityId}`);
  };

  if (isLoading) {
    return (
      <div className={styles.page}>
        <Container size="full" padding className={styles.loadingContainer}>
          <div className={styles.loadingPulse} />
        </Container>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <Container size="full" padding>
          <Stack direction="row" justify="space-between" align="center">
            <Heading level={1} size="lg" color="primary">
              Social
            </Heading>
            <Stack direction="row" gap={2}>
              <button 
                className={styles.iconButton}
                aria-label="Search friends"
              >
                <Search size={22} strokeWidth={2} />
              </button>
              <button 
                className={styles.iconButton}
                aria-label="Add friend"
              >
                <UserPlus size={22} strokeWidth={2} />
              </button>
            </Stack>
          </Stack>
        </Container>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        <Container size="full" padding>
          
          {/* Quick Actions Row */}
          <section className={styles.section}>
            <div className={styles.horizontalScroll}>
              <div className={styles.quickActionsList}>
                <QuickAction 
                  icon="search" 
                  label="Find Friends" 
                  onClick={() => console.log('Find friends')}
                />
                <QuickAction 
                  icon="users" 
                  label="Create Group" 
                  onClick={() => console.log('Create group')}
                />
                <QuickAction 
                  icon="users-round" 
                  label="My Groups" 
                  onClick={() => console.log('My groups')}
                />
                <QuickAction 
                  icon="mail" 
                  label="Invites" 
                  badgeCount={pendingInvites}
                  onClick={() => console.log('Invites')}
                />
              </div>
            </div>
          </section>

          {/* Stories/Status Row */}
          <section className={styles.section}>
            <div className={styles.horizontalScroll}>
              <div className={styles.storiesList}>
                {friends.filter(f => f.isOnline).map((friend) => (
                  <div key={friend.id} className={styles.storyItem}>
                    <div className={styles.storyAvatar}>
                      {friend.name.charAt(0).toUpperCase()}
                      <span className={styles.storyStatusDot} />
                    </div>
                    <Text size="xs" color="muted" align="center" className={styles.storyName}>
                      {friend.name.split(' ')[0]}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Friends List Section */}
          <section className={styles.section}>
            <Stack direction="row" justify="space-between" align="center" className={styles.sectionHeader}>
              <Stack direction="row" gap={2} align="center">
                <Heading level={2} size="md" color="primary">
                  Friends
                </Heading>
                <span className={styles.countBadge}>
                  {friends.length}
                </span>
              </Stack>
              <button 
                className={styles.seeAllButton}
                onClick={handleSeeAllFriends}
              >
                See All
              </button>
            </Stack>
            
            <div className={styles.friendsList}>
              {friends.slice(0, 4).map((friend, index) => (
                <FriendCard 
                  key={friend.id}
                  friend={friend}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onMessage={() => handleMessageFriend(friend.id)}
                  onMore={() => handleMoreOptions(friend.id)}
                />
              ))}
            </div>
          </section>

          {/* Activity Feed Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <Heading level={2} size="md" color="primary">
                Friend Activity
              </Heading>
            </div>
            
            <div className={styles.activityList}>
              {activityFeed.map((activity, index) => (
                <ActivityItem 
                  key={activity.id}
                  activity={activity}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onAction={() => handleActivityAction(activity.id)}
                />
              ))}
            </div>
          </section>

          {/* Groups Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <Heading level={2} size="md" color="primary">
                Your Groups
              </Heading>
            </div>
            
            <div className={styles.horizontalScroll}>
              <div className={styles.groupsList}>
                {groups.map((group, index) => (
                  <GroupCard 
                    key={group.id}
                    group={group}
                    style={{ animationDelay: `${index * 100}ms` }}
                    onClick={() => handleGroupClick(group.id)}
                  />
                ))}
                <GroupCard 
                  isDiscover 
                  onClick={handleDiscoverGroups}
                />
              </div>
            </div>
          </section>

          {/* Suggested Connections Section */}
          <section className={`${styles.section} ${styles.suggestionsSection}`}>
            <div className={styles.sectionHeader}>
              <Heading level={2} size="md" color="primary">
                People You May Know
              </Heading>
            </div>
            
            <div className={styles.horizontalScroll}>
              <div className={styles.suggestionsList}>
                {suggestions.map((suggestion, index) => (
                  <SuggestionCard 
                    key={suggestion.id}
                    suggestion={suggestion}
                    isAdded={addedSuggestions.includes(suggestion.id)}
                    style={{ animationDelay: `${index * 100}ms` }}
                    onAdd={() => handleAddFriend(suggestion.id)}
                  />
                ))}
              </div>
            </div>
          </section>

        </Container>
      </main>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

export default SocialPage;
